import React, { useCallback, useEffect, useState, useMemo } from "react";
import { getPaymentList } from "../../services/transaction";
import toast from "react-hot-toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Toggle from "../../components/Toggle";
import { useAuth } from "../../context/AuthContextProvider";
import { CiLink } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import LoadingScreen from "../../components/LoadingScreen";
import NoPaymentLinks from "./NoPaymentLinks";

const PaymentLinks = () => {
  const navigate = useNavigate();
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const { globalProfile } = useAuth();

  const handleCreateLink = () => {
    navigate("/payments/payment-links/create-link");
  };

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPaymentList();
      setPaymentLinks(res.data || []);
      setLoading(false);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments, globalProfile]);

  const filteredLinks = useMemo(() => {
    if (!status) return paymentLinks;
    return paymentLinks.filter(
      (p) => p.status?.toLowerCase() === status.toLowerCase()
    );
  }, [paymentLinks, status]);

  if (loading) {
    return <LoadingScreen title="Payments" />;
  }

  if (paymentLinks.length === 0) {
    return <NoPaymentLinks />;
  }

  const handleView = (id) => {
    navigate(`/payments/payment-links/payment-link/${id}`);
  };

  return (
    <div className="">
      <div className="flex flex-col xl:flex-row items-start justify-between">
        <h1 className="text-navy-blue text-2xl font-bold">Payment Links</h1>

        <div className="flex sm:flex-row flex-col justify-center items-center">
          <Toggle />
        </div>
      </div>
      <div className="xl:flex items-center justify-end mt-10">
        <div className="relative ml-3 sm:ml-0">
          <button
            onClick={handleCreateLink}
            className="flex items-center justify-center gap-2 text-white bg-primary lg:w-[165px] w-[130px] h-[42px] rounded-[10px]"
          >
            <CiLink className="text-2xl" />
            Create
          </button>
        </div>
      </div>

      <div className="bg-white mr-5 md:mr-0 sm:mt-4 sm:ml-3 sm:px-7 md:px-0 lg:px-7 sm:py-5">
        <div className="flex items-center sm:justify-between mb-4">
          <div className="flex flex-col-reverse sm:flex-row sm:items-center">
            <p className="border-r pr-4 text-sm font-bold text-black-shade">
              Payment Links
            </p>

            <div className="flex sm:flex-row items-center border-r px-4">
              <div className="flex items-center gap-1 mr-2">
                <div className="rounded-full bg-green-400 w-[6px] h-[6px]"></div>
                <p
                  className={`text-[12px] font-normal cursor-pointer ${
                    status === "APPROVED" ? "font-bold text-green-600" : "text-black-shade"
                  }`}
                  onClick={() => setStatus((prev) => (prev === "APPROVED" ? null : "APPROVED"))}
                >
                  Approved
                </p>
              </div>
              <div className="flex items-center gap-1 mr-2">
                <div className="rounded-full bg-red-400 w-[6px] h-[6px]"></div>
                <p
                  className={`text-[12px] font-normal cursor-pointer ${
                    status === "Declined" ? "font-bold text-red-600" : "text-black-shade"
                  }`}
                  onClick={() => setStatus((prev) => (prev === "Declined" ? null : "Declined"))}
                >
                  Declined
                </p>
              </div>
              <div className="flex items-center gap-1">
                <div className="rounded-full bg-black-shade w-[6px] h-[6px]"></div>
                <p
                  className={`text-[12px] font-normal cursor-pointer ${
                    status === "Initiated" ? "font-bold text-blue-600" : "text-black-shade"
                  }`}
                  onClick={() => setStatus((prev) => (prev === "Initiated" ? null : "Initiated"))}
                >
                  Initiated
                </p>
              </div>

              {status && (
                <button
                  onClick={() => setStatus(null)}
                  className="ml-3 text-xs text-gray-500 underline"
                >
                  Clear filter
                </button>
              )}
            </div>
          </div>
        </div>

        <DataTable
          value={filteredLinks}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 20]}
          emptyMessage="No payment links found."
          onRowClick={(e) => handleView(e.data.id)}
          rowClassName={() => "cursor-pointer hover:bg-gray-50 transition-colors"}
          className="p-datatable-sm"
          responsiveLayout="scroll"
        >
          <Column
            field="id"
            header="#"
            sortable
            body={(row) => <div className="py-2">{row.id}</div>}
          />
          <Column
            field="link"
            header="Payment Link"
            body={(row) => (
              <div className="py-2 truncate max-w-[200px]" title={row.link}>
                {row.link}
              </div>
            )}
          />
          <Column
            field="description"
            header="Description"
            sortable
            body={(row) => <div className="py-2">{row.description}</div>}
          />
          <Column
            field="dateCreated"
            header="Date Created"
            sortable
            body={(row) =>
              row.dateCreated
                ? new Date(row.dateCreated).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "-"
            }
          />
          <Column
            field="currency"
            header="Currency"
            sortable
            body={(row) => (
              <div className="text-primary text-center bg-[rgba(var(--primary-rgb),0.3)] w-[70px] h-[30px] rounded-lg py-1 font-medium">
                {row.currency}
              </div>
            )}
          />
          <Column
            field="amount"
            header="Amount"
            sortable
            body={(row) =>
              row.amount != null ? Number(row.amount).toFixed(2) : "-"
            }
          />
        </DataTable>
      </div>
    </div>
  );
};

export default PaymentLinks;
